import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface GraphicDownload {
  id: string;
  shelter_id: string;
  shelter_name: string;
  needs_text: string;
  format: string;
  downloaded_at: string;
  is_admin_download: boolean;
}

export const useGraphicDownloads = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["graphic-downloads"],
    queryFn: async (): Promise<GraphicDownload[]> => {
      const { data, error } = await supabase
        .from("graphic_downloads")
        .select("*")
        .order("downloaded_at", { ascending: false })
        .limit(100);

      if (error) throw error;
      return (data || []) as GraphicDownload[];
    },
    enabled,
  });
};

export const useDeleteGraphicDownload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("graphic_downloads")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["graphic-downloads"] });
    },
  });
};

/**
 * Checks if current user is an admin (for marking admin downloads)
 */
const checkIsAdmin = async (): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    
    const { data, error } = await supabase.rpc("has_role", {
      _user_id: user.id,
      _role: "admin",
    });
    
    if (error) return false;
    return data === true;
  } catch {
    return false;
  }
};

export const saveGraphicDownload = async (
  shelterId: string,
  shelterName: string,
  needsText: string,
  format: string
): Promise<void> => {
  // Check if current user is admin
  const isAdmin = await checkIsAdmin();
  
  const { error } = await supabase.from("graphic_downloads").insert({
    shelter_id: shelterId,
    shelter_name: shelterName,
    needs_text: needsText,
    format,
    is_admin_download: isAdmin,
  });

  if (error) {
    console.error("Failed to save graphic download:", error);
    // Don't throw - we don't want to interrupt the download if logging fails
  }
};
